import {
  BackgroundColor,
  Color,
  Theme,
  type CssColor,
} from '@adobe/leonardo-contrast-colors'
import type { Payload } from 'recharts/types/component/DefaultTooltipContent.js'

import {
  ApiRateLimitStat,
  ApiRequestLimitType,
  DailyApiUsage,
} from '@tribeplatform/gql-client/types'

const DAILY_DURATION = 86_400

export const getDailyLimitStatForUsageType = (
  apiQuotas: ApiRateLimitStat[],
  type: ApiRequestLimitType,
): ApiRateLimitStat => {
  return apiQuotas.find(
    q => q.consumableType === type && q.duration === DAILY_DURATION,
  )
}

export const partitionUsageDataByType = (apiUsages: DailyApiUsage[]) => {
  return apiUsages.reduce(
    (acc, usage) => {
      if (usage.usageType === ApiRequestLimitType.Request) {
        return {
          ...acc,
          requestCountUsages: [...acc.requestCountUsages, usage],
        }
      }
      if (usage.usageType === ApiRequestLimitType.Complexity) {
        return {
          ...acc,
          complexityUsages: [...acc.complexityUsages, usage],
        }
      }
      return acc
    },
    {
      requestCountUsages: [] as Array<DailyApiUsage>,
      complexityUsages: [] as Array<DailyApiUsage>,
    },
  )
}

type ChartData = {
  date: Date
  [key: string]: unknown
}

export const dailyUsageToChartDataMapper = ({
  date,
  values,
}: DailyApiUsage): ChartData => {
  return {
    date: new Date(date),
    ...values.reduce(
      (acc, { clientId, clientName, value }) => {
        acc[clientName || clientId] = value
        return acc
      },
      {} as Record<string, number>,
    ),
  }
}

export const sortApiConsumersByRequestCount = (chartData: DailyApiUsage[]) => {
  const apiConsumers = chartData
    .filter(d => d.usageType === ApiRequestLimitType.Request)
    .map(d => d.values)
    .reduce(
      (acc, values) => {
        return values.reduce((res, params) => {
          const { value, clientName, clientId } = params
          const key = clientName || clientId
          return {
            ...res,
            [key]: +value + (res[key] ?? 0),
          }
        }, acc)
      },
      {} as Record<string, number>,
    )

  const keys = Object.keys(apiConsumers).sort((a, b) => {
    return apiConsumers[b] - apiConsumers[a]
  })
  return keys
}

/**
 * Plasma color palette generated using https://waldyrious.net/viridis-palette-generator/
 */
const PlasmaColorKeys: CssColor[] = [
  '#f0f921',
  '#fdc527',
  '#f89540',
  '#e66c5c',
  '#cc4778',
  '#aa2395',
  '#7e03a8',
  '#4c02a1',
  '#0d0887',
]

export const generateHeatmapColors = (count: number) => {
  const backgroundColor = getComputedStyle(document.body).getPropertyValue(
    '--bm-color-surface',
  ) as CssColor

  const bg = new BackgroundColor({
    name: 'bg',
    colorKeys: [backgroundColor as CssColor],
    ratios: [1],
  })

  const ratios = generateReverseRange(count)
  const shade = new Color({
    name: 'primaryShade',
    colorKeys: PlasmaColorKeys,
    colorspace: 'HSL',
    ratios,
  })

  const theme = new Theme({
    colors: [shade],
    backgroundColor: bg,
    lightness: 80,
  })
  return theme
}

const generateReverseRange = (count: number) => {
  return Array.from({ length: count }, (_, i) => count - i)
}

export const calculatePayloadSum = (payload: Payload<number, string>[]) => {
  return payload.reduce((sum, { value }) => {
    return sum + value
  }, 0)
}
